<template>
    <div data-app>
      <div
        role="alert"
        v-bind:class="{ show: errors.length }"
        class="alert fade alert-danger"
      >
        <div class="alert-text" v-for="(error, i) in errors" :key="i">
          {{ error }}
        </div>
      </div>
      <v-card>
        <v-card-title>Attack Detection</v-card-title>
        <v-card-text>
          <v-dialog v-model="globalSettingsDialog" max-width="600px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" dark v-bind="attrs" v-on="on" class="mr-2">
                <v-icon left>mdi-cog</v-icon>
                Edit Global Settings
              </v-btn>
            </template>
            <v-card>
              <v-card-title>Global Attack Detection Settings</v-card-title>
              <v-card-text>
                <v-container>
                  <v-row dense>
                    <v-col cols="12">
                      <v-checkbox v-model="globalSettings.enabled" label="Enable Attack Detection" />
                      <v-checkbox v-model="globalSettings.auto_prefixes" label="Auto-detect Prefixes" />
                    </v-col>

                    <v-col cols="12">
                      <v-combobox
                        v-model="globalSettings.exclude_prefixes"
                        label="Excluded Prefixes"
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        :error-messages="excludePrefixErrors"
                        @input="validatePrefixes"
                      >
                        <template v-slot:selection="{ attrs, item, select, selected }">
                          <v-chip
                            v-bind="attrs"
                            :input-value="selected"
                            close
                            @click="select"
                            @click:close="removeExcludePrefix(item)"
                          >
                            {{ item }}
                          </v-chip>
                        </template>
                      </v-combobox>
                    </v-col>

                    <v-col cols="12">
                      <v-subheader>Notifications</v-subheader>
                      <v-text-field
                        v-model="globalSettings.notify.email"
                        label="Email Address"
                        :rules="[v => !v || /.+@.+\..+/.test(v) || 'Email must be valid']"
                      />
                      <v-text-field
                        v-model="globalSettings.notify.webhook"
                        label="Webhook URL"
                        :rules="[v => !v || /^https?:\/\/.+/.test(v) || 'Must be a valid URL']"
                      />
                    </v-col>

                    <v-col cols="12">
                      <v-subheader>Default Per-Host Thresholds</v-subheader>
                      <v-row dense>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="globalSettings.default_per_host_threshold.mbps"
                            label="Mbps"
                            type="number"
                            min="0"
                            :rules="[v => v === null || v === undefined || v === '' || v >= 0 || 'Must be greater than or equal to 0']"
                            clearable
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="globalSettings.default_per_host_threshold.pps"
                            label="Packets per second"
                            type="number"
                            min="0"
                            :rules="[v => v === null || v === undefined || v === '' || v >= 0 || 'Must be greater than or equal to 0']"
                            clearable
                          />
                        </v-col>
                      </v-row>
                    </v-col>

                    <v-col cols="12">
                      <v-subheader>Default Per-Network Thresholds</v-subheader>
                      <v-row dense>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="globalSettings.default_per_net_threshold.mbps"
                            label="Mbps"
                            type="number"
                            min="0"
                            :rules="[v => v === null || v === undefined || v === '' || v >= 0 || 'Must be greater than or equal to 0']"
                            clearable
                          />
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model.number="globalSettings.default_per_net_threshold.pps"
                            label="Packets per second"
                            type="number"
                            min="0"
                            :rules="[v => v === null || v === undefined || v === '' || v >= 0 || 'Must be greater than or equal to 0']"
                            clearable
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="globalSettingsDialog = false">Cancel</v-btn>
                <v-btn color="blue darken-1" text @click="saveGlobalSettings">Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card-text>
        <v-data-table
          v-model="selected"
          :loading="loading"
          :headers="headers"
          :items="thresholds"
          :footer-props="{
            itemsPerPageOptions: [10, 20, 50, 100],
            showFirstLastPage: true,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right',
            prevIcon: 'mdi-chevron-left',
            nextIcon: 'mdi-chevron-right',
          }"
          :items-per-page="itemsPerPage"
          :server-items-length="totalRules"
          @update:page="onPageChange"
          @update:items-per-page="onItemsPerPageChange"
          item-key="id"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat color="white">
              <v-spacer></v-spacer>
              <v-dialog v-model="dialog" max-width="600px">
                <template v-slot:activator="{ on }">
                  <v-btn color="primary" dark class="mb-2" v-on="on"
                    >New Threshold</v-btn
                  >
                </template>
                <v-card>
                  <v-card-title>
                    <span class="headline">{{ formTitle }}</span>
                  </v-card-title>
  
                  <v-card-text>
                    <v-form ref="newForm" lazy-validation>
                      <v-container>
                        <v-row dense>
                          <v-col cols="12" sm="6">
                            <v-combobox
                              v-model="editedItem.prefixes"
                              :rules="[v => v.length > 0 || 'At least one prefix is required']"
                              label="IP Prefixes"
                              multiple
                              chips
                              small-chips
                              deletable-chips
                              :error-messages="prefixErrors"
                              @input="validatePrefixes"
                            >
                              <template v-slot:selection="{ attrs, item, select, selected }">
                                <v-chip
                                  v-bind="attrs"
                                  :input-value="selected"
                                  close
                                  @click="select"
                                  @click:close="removePrefix(item)"
                                >
                                  {{ item }}
                                </v-chip>
                              </template>
                            </v-combobox>
                          </v-col>
                          <v-col cols="12">
                            <v-subheader>Per-Host Thresholds</v-subheader>
                            <v-row dense>
                              <v-col cols="6">
                                <v-text-field
                                  v-model.number="editedItem.per_host_threshold.mbps"
                                  label="Mbps"
                                  type="number"
                                  min="0"
                                  :rules="[v => v === null || v === undefined || v === '' || v >= 0 || 'Must be greater than or equal to 0']"
                                  clearable
                                ></v-text-field>
                              </v-col>
                              <v-col cols="6">
                                <v-text-field
                                  v-model.number="editedItem.per_host_threshold.pps"
                                  label="Packets per second"
                                  type="number"
                                  min="0"
                                  :rules="[v => v === null || v === undefined || v === '' || v >= 0 || 'Must be greater than or equal to 0']"
                                  clearable
                                ></v-text-field>
                              </v-col>
                            </v-row>
                          </v-col>

                          <v-col cols="12">
                            <v-subheader>Per-Network Thresholds</v-subheader>
                            <v-row dense>
                              <v-col cols="6">
                                <v-text-field
                                  v-model.number="editedItem.per_net_threshold.mbps"
                                  label="Mbps"
                                  type="number"
                                  min="0"
                                  :rules="[v => v === null || v === undefined || v === '' || v >= 0 || 'Must be greater than or equal to 0']"
                                  clearable
                                ></v-text-field>
                              </v-col>
                              <v-col cols="6">
                                <v-text-field
                                  v-model.number="editedItem.per_net_threshold.pps"
                                  label="Packets per second"
                                  type="number"
                                  min="0"
                                  :rules="[v => v === null || v === undefined || v === '' || v >= 0 || 'Must be greater than or equal to 0']"
                                  clearable
                                ></v-text-field>
                              </v-col>
                            </v-row>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-form>
                  </v-card-text>
  
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="blue darken-1" text @click="closeDialog"
                      >Cancel</v-btn
                    >
                    <v-btn color="blue darken-1" text @click="saveThreshold"
                      >Save</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-toolbar>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
            <v-icon small @click="editItem(item)">mdi-pencil</v-icon>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </template>
  
  <script>
  import api from "@/common/api.service";
  import Errors from "@/common/errors";
  import ipAddr from "ip-address";
  import { debounce } from "lodash";
  
  export default {
    mounted() {
      this.fetchThresholds();
      this.fetchGlobalSettings();
    },
  
    data() {
      return {
        selected: [],
        headers: [
          { text: "ID", sortable: false, align: "left", value: "id" },
          { text: "Prefixes", value: "prefixes" },
          { text: "Per-Host PPS", value: "per_host_pps_threshold" },
          { text: "Per-Host Mbps", value: "per_host_mbps_threshold" },
          { text: "Per-Network PPS", value: "per_net_pps_threshold" },
          { text: "Per-Network Mbps", value: "per_net_mbps_threshold" },
          { text: "Actions", value: "actions", sortable: false },
        ],
        thresholds: [],
        globalSettings: {
          enabled: false,
          auto_prefixes: false,
          exclude_prefixes: [],
          notify: {
            email: "",
            webhook: ""
          },
          default_per_host_threshold: {
            "mbps": 0,
            "pps": 0
          },
          default_per_net_threshold: {
            "mbps": 0,
            "pps": 0
          }
        },
        searchCounter: 0,
        errors: [],
        loading: true,
        dialog: false,
        editedIndex: -1,
        editedItem: {
          id: "",
          prefixes: [],
          per_host_threshold: {
            "mbps": 0,
            "pps": 0
          },
          per_net_threshold: {
            "mbps": 0,
            "pps": 0
          }
        },
        ipRules: [
          (v) => !!v || "IP address is required",
          (v) => (!!v && this.ipValidator(v)) || "IP address is invalid",
        ],
        prefixErrors: [],
        itemsPerPage: 10,
        totalRules: 0,
        currentPage: 1,
        globalSettingsDialog: false,
        excludePrefixErrors: [],
      };
    },
  
    watch: {
      dialog(val) {
        if (!val) this.closeDialog();
      },
    },
  
    computed: {
      formTitle() {
        return this.editedIndex === -1 ? "New Item" : "Edit Item";
      },
    },
  
    methods: {
      editItem(item) {
        this.editedIndex = this.thresholds.indexOf(item);
        this.editedItem = {
          id: item.id,
          prefixes: item.prefixes,
          per_host_threshold: {
            mbps: item.per_host_mbps_threshold,
            pps: item.per_host_pps_threshold
          },
          per_net_threshold: {
            mbps: item.per_net_mbps_threshold,
            pps: item.per_net_pps_threshold
          }
        };        
        this.dialog = true;
      },  
  
      ipValidator(ip) {
        const ipValidator = new ipAddr.Address4(ip) || new ipAddr.Address6(ip);
  
        if (!ipValidator.isValid()) {
          return false;
        }
  
        if (ip.includes("/")) {
          return (
            ipValidator.startAddress().correctForm() === ipValidator.correctForm()
          );
        } else {
          return true;
        }
      },

      removePrefix(prefix) {
        this.editedItem.prefixes = this.editedItem.prefixes.filter(p => p !== prefix);
      },

      removeExcludePrefix(prefix) {
        this.globalSettings.exclude_prefixes = this.globalSettings.exclude_prefixes.filter(p => p !== prefix);
      },

      validatePrefixes(prefixes) {
        this.prefixErrors = [];
        if (!prefixes) return;
        
        prefixes.forEach(prefix => {
          if (!this.ipValidator(prefix)) {
            this.prefixErrors.push(`Invalid IP prefix: ${prefix}`);
            // Remove invalid prefix
            const index = this.editedItem.prefixes.indexOf(prefix);
            if (index > -1) {
              this.editedItem.prefixes.splice(index, 1);
            }
          }
        });
      },
  
      fetchThresholds() {
        this.searchCounter++;
        const currentRequest = this.searchCounter;
  
        this.loading = true;
        const queryParams = new URLSearchParams();
        queryParams.append("page", this.currentPage);
        queryParams.append("size", this.itemsPerPage);
  
        api
          .query(`v2/attack_detection/thresholds?${queryParams.toString()}`)
          .then((response) => {
            if (this.searchCounter == currentRequest) {
              this.thresholds = response.data.data.map(item => ({
                id: item.id,
                prefixes: item.prefixes,
                per_host_pps_threshold: item.per_host_threshold.pps,
                per_net_pps_threshold: item.per_net_threshold.pps,
                per_host_mbps_threshold: item.per_host_threshold.mbps,
                per_net_mbps_threshold: item.per_net_threshold.mbps
              }));
              this.totalRules = response.data.total;
            }
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
          .finally(() => {
            this.loading = false;
          });
      },

      fetchGlobalSettings() {
        api
          .query("v2/attack_detection")
          .then((response) => {
            this.globalSettings = response.data;
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
      },
  
      debouncedFetchRules: debounce(function () {
        this.currentPage = 1; // Reset to first page when search changes
        this.fetchRules();
      }, 300),
      onPageChange(page) {
        this.currentPage = page;
        this.fetchRules();
      },
  
      onItemsPerPageChange(size) {
        this.itemsPerPage = size;
        this.currentPage = 1; // Reset to the first page
        this.fetchThresholds();
      },
  
      closeDialog() {
        this.dialog = false;
        this.editedItem = {
          id: "",
          per_host_threshold: {
            "mbps": 0,
            "pps": 0
          },
          per_net_threshold: {
            "mbps": 0,
            "pps": 0
          },
          prefixes: []
        };
        this.editedIndex = -1;
      },

      normalizeNullableNumber(value) {
        return value === "" ? null : value;
      },

      normalizeThresholdPair(thresholds) {
        thresholds.mbps = this.normalizeNullableNumber(thresholds.mbps);
        thresholds.pps = this.normalizeNullableNumber(thresholds.pps);
      },
  
      saveThreshold() {
        if (!this.$refs.newForm.validate()) return;

        this.normalizeThresholdPair(this.editedItem.per_host_threshold);
        this.normalizeThresholdPair(this.editedItem.per_net_threshold);

        if (this.editedIndex === -1) {
          api
          .post("v2/attack_detection/thresholds", this.editedItem)
          .then((_) => {
            this.fetchThresholds();
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
          .finally(() => {
            this.closeDialog();
          });
        } else {
          api
          .patch(`v2/attack_detection/thresholds/${this.editedItem.id}`, this.editedItem)
          .then((_) => {
            this.fetchThresholds();
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)))
          .finally(() => {
            this.closeDialog();
          });
        }
      },

      saveGlobalSettings() {
        this.normalizeThresholdPair(this.globalSettings.default_per_host_threshold);
        this.normalizeThresholdPair(this.globalSettings.default_per_net_threshold);

        api
          .patch("v2/attack_detection", this.globalSettings)
          .then((_) => {
            this.fetchGlobalSettings();
            this.globalSettingsDialog = false;
          })
          .catch((error) => this.errors.push(...Errors.extractErrorMessage(error)));
      },
  
      deleteItem(item) {
        if (confirm("Are you sure you want to delete this item?")) {
          api.delete(`v2/attack_detection/thresholds/${item.id}`).then(() => {
            this.fetchThresholds();
          });
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .v-chip {
    margin: 4px;
  }
  .error--text {
    color: #ff5252 !important;
  }
  </style>
  
